import {FormArray, FormControl, FormGroup, Validators} from "@angular/forms";
import {CustomValidators} from "../../helpers/custom-validators";
import {ExerciseValidationService} from "../../helpers/exercise-validation.service";
import {IRegularExerciseConfig, IRegularExerciseForm} from "../../../shared/interfaces/forms/exercises/IRegularExerciseForm";
import {IVerticalForm} from "../../../shared/interfaces/forms/exercises/IVerticalForm";
import {IReactForm} from "../../../shared/interfaces/forms/exercises/IReactForm";
import {ICountdownForm} from "../../../shared/interfaces/forms/exercises/ICountdownForm";
import {ICountForm} from "../../../shared/interfaces/forms/exercises/ICountForm";
import {ISequenceForm} from "../../../shared/interfaces/forms/exercises/ISequenceForm";
import {EBodyPart} from "../../../shared/enums/EBodyPart";
import {IAgilityForm} from "../../../shared/interfaces/forms/exercises/IAgilityForm";
import {ICompanionForm} from "../../../shared/interfaces/forms/exercises/ICompanionForm";
import {ECompanionMode} from "../../../shared/enums/ECompanionMode";
import {ECompanionAgilityType} from "../../../shared/enums/ECompanionAgilityType";
import {ECompanionNumberType} from "../../../shared/enums/ECompanionNumberType";
import {ECompanionCommunicationType} from "../../../shared/enums/ECompanionCommunicationType";
import {ECompanionSimultaneousType} from "../../../shared/enums/ECompanionSimultaneousType";
import {EStroopEffectType} from "../../../shared/enums/EStroopEffectType";
import {EAgilityCustomType} from "../../../shared/enums/EAgilityCustomType";
import {integerValidator} from "../../../shared/validators/integer.validator";

export interface IRegularExerciseConfigForm {
    id?: FormControl<string>;
    playlistConfigId?: FormControl<string>;
    order?: FormControl<number>;
    isSelected?: FormControl<boolean>;
    sets?: FormControl<number>;
    difficulty: FormControl<number>;
    verticalForm: FormGroup<IVerticalForm>;
    countdownForm: FormGroup<ICountdownForm>;
    reactForm: FormGroup<IReactForm>;
    countForm: FormGroup<ICountForm>;
    sequenceForm: FormGroup<ISequenceForm>;
    agilityForm: FormGroup<IAgilityForm>;
    companionForm: FormGroup<ICompanionForm>;
}

export class RegularExerciseFormFactory {
  static create(): FormGroup<IRegularExerciseForm> {
    return new FormGroup<IRegularExerciseForm>({
      active: new FormControl(true, [Validators.required]),
      name: new FormControl("", [Validators.required]),
      description: new FormControl("", [Validators.maxLength(4000)]),
      originalDescriptionHtml: new FormControl(""),
      category: new FormControl(
        {
          id: null,
          name: null,
        },
        [CustomValidators.requiredCategoryValidator],
      ),
        bodyPart: new FormControl<EBodyPart>(null),
      type: new FormControl(0, [Validators.required]),
      video: new FormGroup({
          file: new FormControl<File>(null),
          fileUrl: new FormControl<string>('')
      }),
      imgThumb: new FormControl(""),
      config: RegularExerciseConfigFormFactory.create() as FormGroup<IRegularExerciseConfig>
    });
  }
}

export class RegularExerciseConfigFormFactory {
    public static create(): FormGroup<IRegularExerciseConfigForm> {
        return new FormGroup<IRegularExerciseConfigForm>({
            id: new FormControl<string>(null),
            playlistConfigId: new FormControl<string>(null),
            isSelected: new FormControl<boolean>(false),
            sets: new FormControl<number>(1, [Validators.required, Validators.min(0), Validators.max(25)]),
            difficulty: new FormControl(2, [Validators.required]),
            verticalForm: new FormGroup<IVerticalForm>({
                verticalJumpType: new FormControl(0, [Validators.required]),
                numberOfLegs: new FormControl(1, [Validators.required]),
                numberOfJumps: new FormControl(1, [
                    Validators.required,
                    integerValidator(),
                    Validators.min(1),
                    Validators.max(30),
                ]),
                isVerticalReact: new FormControl(false, [Validators.required]),
                reactTo: new FormArray([
                    new FormControl(false),
                    new FormControl(false),
                    new FormControl(false),
                    new FormControl(false),
                    new FormControl(false),
                    new FormControl(false),
                    new FormControl(false),
                    new FormControl(false),
                ]),
                dontReactTo: new FormArray([
                    new FormControl(false),
                    new FormControl(false),
                    new FormControl(false),
                    new FormControl(false),
                    new FormControl(false),
                    new FormControl(false),
                    new FormControl(false),
                    new FormControl(false),
                ]),

            }, [
                ExerciseValidationService.minOneReact(),
            ],),
            countdownForm: new FormGroup<ICountdownForm>({
                startType: new FormControl(0, [Validators.required]),
                startValue: new FormControl(null, [
                    integerValidator(),
                    Validators.min(1),
                    Validators.max(999)
                ]),
                endType: new FormControl(1, [Validators.required]),
                endValue: new FormControl(null, [
                    Validators.required,
                    integerValidator(),
                    Validators.min(1),
                    Validators.max(999),
                ]),
            }, [ExerciseValidationService.startValueValidation()]),
            reactForm: new FormGroup<IReactForm>(
                {
                    delay: new FormControl(0, [
                        Validators.required,
                        Validators.min(0),
                        Validators.max(10),
                    ]),
                    timerPrompt: new FormControl(0, [
                        Validators.min(-1),
                        Validators.max(10),
                    ]),
                    liftTime: new FormControl(false, [Validators.required]),
                    footFireActivity: new FormControl(false, [Validators.required]),
                    stroopEffectType: new FormControl(EStroopEffectType.NONE, Validators.required),
                    neurocognitiveType: new FormControl(0, Validators.required),
                    isolatedReact: new FormControl(false, Validators.required),
                    reactTo: new FormArray([
                        new FormControl(false),
                        new FormControl(false),
                        new FormControl(false),
                        new FormControl(false),
                        new FormControl(false),
                        new FormControl(false),
                        new FormControl(false),
                        new FormControl(false),
                    ]),
                    dontReactTo: new FormArray([
                        new FormControl(false),
                        new FormControl(false),
                        new FormControl(false),
                        new FormControl(false),
                        new FormControl(false),
                        new FormControl(false),
                        new FormControl(false),
                        new FormControl(false),
                    ]),
                    activeSensors: new FormArray([
                        new FormControl(true),
                        new FormControl(true),
                        new FormControl(true),
                        new FormControl(true),
                        new FormControl(true),
                    ]),
                },
                [
                    ExerciseValidationService.neurocognitiveValidation(),
                    ExerciseValidationService.activeSensorValidation(),
                ],
            ),
            countForm: new FormGroup<ICountForm>({
                countType: new FormControl(0, [Validators.required]),
            }),
            sequenceForm: new FormGroup<ISequenceForm>(
                {
                    delay: new FormControl(0, [Validators.required, Validators.min(0), Validators.max(10)]),
                    timerPrompt: new FormControl(0, [
                        Validators.min(-1),
                        Validators.max(10),
                    ]),
                    sequenceSensors: new FormArray<FormControl<number>>(
                        [],
                        [
                            Validators.required,
                            Validators.maxLength(200),
                        ],
                    ),
                },
                [ExerciseValidationService.sequenceValidation()],
            ),
            agilityForm: new FormGroup<IAgilityForm>(
                {
                    delay: new FormControl(0, [Validators.required, Validators.min(0), Validators.max(10)]),
                    agilityCustomType: new FormControl(0, [Validators.required]),
                    agilitySensors: new FormArray<FormControl<number>>(
                        [],
                        [
                            Validators.required,
                            Validators.maxLength(200),
                        ],
                    ),
                    timerPrompt: new FormControl(0, [
                        Validators.required,
                        Validators.min(0),
                        Validators.max(10)]
                    ),
                },
                [ExerciseValidationService.arrowValidation()],
            ),
            companionForm: new FormGroup<ICompanionForm>({
                companionMode: new FormControl<ECompanionMode>(ECompanionMode.Simultaneous, [Validators.required]),
                numberOfCompanions: new FormControl<number>(1, [Validators.required, integerValidator(), Validators.min(1), Validators.max(3)]),
                numberOfSensors: new FormControl<number>(1, [Validators.required, integerValidator(), Validators.min(1), Validators.max(5)]),
                agilityType: new FormControl<ECompanionAgilityType>(ECompanionAgilityType.Color),
                numberType: new FormControl<ECompanionNumberType>(ECompanionNumberType.SingleValue),
                arrowsType: new FormControl<EAgilityCustomType>(EAgilityCustomType.Sequence, [Validators.required]),
                arrows: new FormArray<FormControl<number>>([], [Validators.maxLength(200)]),
                countIncrementBy: new FormControl<number>(1, [integerValidator(), Validators.min(1), Validators.max(100)]),
                countDirection: new FormControl<'Ascending' | 'Descending'>('Ascending'),
                // These values are only constrained for active Number modes.
                // Keeping the rules on the Companion group preserves iOS mode
                // state without hidden controls invalidating another mode.
                startValue: new FormControl<number>(1),
                endValue: new FormControl<number>(10),
                limitLow: new FormControl<number>(null, [integerValidator(), Validators.min(1), Validators.max(999)]),
                limitHigh: new FormControl<number>(null, [integerValidator(), Validators.min(1), Validators.max(999)]),
                complexityLevel: new FormControl<'Beginner' | 'Intermediate' | 'Advanced'>('Beginner'),
                reactTo: new FormArray([
                    new FormControl(false),
                    new FormControl(false),
                    new FormControl(false),
                    new FormControl(false),
                    new FormControl(false),
                    new FormControl(false),
                    new FormControl(false),
                    new FormControl(false),
                ]),
                dontReactTo: new FormArray([
                    new FormControl(false),
                    new FormControl(false),
                    new FormControl(false),
                    new FormControl(false),
                    new FormControl(false),
                    new FormControl(false),
                    new FormControl(false),
                    new FormControl(false),
                ]),
                delay: new FormControl<number>(0, [Validators.required, Validators.min(0), Validators.max(10)]),
                timerPrompt: new FormControl<number>(0, [Validators.min(0), Validators.max(10)]),
                communicationType: new FormControl<ECompanionCommunicationType>(ECompanionCommunicationType.Alternate, [Validators.required]),
                neurocognitiveType: new FormControl<0 | 1 | 2>(0, [Validators.required]),
                stroopEffectType: new FormControl<EStroopEffectType>(EStroopEffectType.NONE, [Validators.required]),
                isolatedReact: new FormControl<boolean>(false, [Validators.required]),
                activeSensors: new FormArray([
                    new FormControl(false),
                    new FormControl(false),
                    new FormControl(false),
                    new FormControl(false),
                    new FormControl(false),
                ]),
                simultaneousType: new FormControl<ECompanionSimultaneousType>(0),
                simultaneousSensors: new FormArray<FormControl<number>>([], [Validators.maxLength(200)]),
                sequenceSensors: new FormArray<FormControl<number>>([], [Validators.maxLength(200)])
            }, [
                ExerciseValidationService.companionValidation()
            ]),
            order: new FormControl<number>(0)
        });
    }
}
